import { observable, configure, action } from 'mobx';

import util from 'preact-util';

import {
    normalizeUserId,
    idsToFetch,
    chunkIds,
} from '../lib/presence';

configure({ enforceActions: 'always' });

// How long tracked ids are collected before one batched fetch fires. Render
// sites call track() per user row, so a short debounce turns a whole list
// into a single request.
const BATCH_DELAY_MS = 250;

// Presence store for the online/offline dots. Components call track(userId)
// for every user they show (safe from render — it only touches plain
// instance state) and read onlineByUserId[userId] as an observable:
// true = green dot, false = grey dot, undefined = unknown (no dot at all —
// hidden profiles and unknown ids are never in the response).
class PresenceStore {
    @observable onlineByUserId = {};

    // Plain (non-observable) working state — never rendered.
    trackedIds = new Set();

    fetchedAtByUserId = {};

    batchTimer = null;

    track(userId) {
        const id = normalizeUserId(userId);
        if (!id) {
            return;
        }
        this.trackedIds.add(id);
        if (!this.batchTimer) {
            this.batchTimer = setTimeout(() => this.flush(), BATCH_DELAY_MS);
        }
    }

    // Overridable network boundary (stubbed in tests).
    async fetchPresence(ids) {
        return util.fetchApi('/api/users/public/presence', { method: 'GET' }, { ids: ids.join(',') });
    }

    async flush() {
        if (this.batchTimer) {
            clearTimeout(this.batchTimer);
            this.batchTimer = null;
        }
        const now = Date.now();
        const ids = idsToFetch([...this.trackedIds], this.fetchedAtByUserId, now);
        this.trackedIds.clear();
        if (ids.length === 0) {
            return;
        }
        const chunks = chunkIds(ids);
        for (let i = 0; i < chunks.length; i += 1) {
            const chunk = chunks[i];
            // Mark before the request so overlapping flushes can't stampede.
            chunk.forEach(id => {
                this.fetchedAtByUserId[id] = now;
            });
            // eslint-disable-next-line no-await-in-loop
            const response = await this.fetchPresence(chunk);
            if (response?.status === 200 && Array.isArray(response.data)) {
                const map = {};
                response.data.forEach(row => {
                    const id = normalizeUserId(row?.id);
                    if (id) {
                        map[id] = !!row.online;
                    }
                });
                this.applyOnline(map);
            }
        }
    }

    @action
    applyOnline(map) {
        this.onlineByUserId = { ...this.onlineByUserId, ...map };
    }

    @action
    resetForTest() {
        if (this.batchTimer) {
            clearTimeout(this.batchTimer);
            this.batchTimer = null;
        }
        this.onlineByUserId = {};
        this.trackedIds = new Set();
        this.fetchedAtByUserId = {};
        delete this.fetchPresence;
    }
}

const store = new PresenceStore();
export default store;
